import { useEffect, useState } from 'react';

import { Col, Row } from 'reactstrap';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import { TextUs } from 'src/app/components/Elements/Location/TextUs';
import Spinner from 'src/app/components/Elements/Spinner';
import { useFacilityByAccountId } from 'src/app/hooks/data/facility';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import { formatHours } from 'src/app/utils/facilityHours';
import { CAPSULE_HELP_EMAIL, IS_MOBILE, WEEK } from 'src/consts';

import { LeftSpan, RightSpan, StoreInfoContainer, StoreInfoRow } from './styles';
import styles from './styles.module.scss';

const Contact = ({ dispatchOpenChat }) => {
  useSegmentTrack(UNIFIED.viewContactUs);

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));

  const { data: facility, isLoading } = useFacilityByAccountId(user ? parseInt(user.accountId) : 0);

  const [operationHours, setOperationHours] = useState({});

  useEffect(() => {
    if (facility) {
      setOperationHours(formatHours(facility.hours));
    }
  }, [facility]);

  const handleOpenLiveChat = e => {
    e.preventDefault();
    dispatchOpenChat();
  };

  if (isLoading) return <Spinner centered />;

  return (
    <div className={styles.contactPage}>
      <Row>
        <Col xs="12" md="9" lg="7">
          <div className={styles.bannerContainer}>
            <h1>
              Contact us
              <img src="/public/icon-questions.svg" className={styles.questionsIcon} alt="" />
            </h1>
            <div className="normal">Our friendly pharmacists are always here to help.</div>
          </div>
          <Card>
            <CardItem>
              <StoreInfoContainer>
                <StoreInfoRow>
                  <LeftSpan data-cy="hours-line-one-day">{WEEK.DAYS}:</LeftSpan>
                  <RightSpan data-cy="hours-line-one-time">{operationHours[WEEK.DAYS]}</RightSpan>
                </StoreInfoRow>
                <StoreInfoRow>
                  <LeftSpan data-cy="hours-line-two-day">
                    {operationHours[WEEK.ENDS] ? WEEK.ENDS : WEEK.SATURDAY}:
                  </LeftSpan>
                  <RightSpan data-cy="hours-line-two-time">
                    {operationHours[WEEK.ENDS] ?? operationHours[WEEK.SATURDAY]}
                  </RightSpan>
                </StoreInfoRow>
                {!operationHours[WEEK.ENDS] && (
                  <StoreInfoRow>
                    <LeftSpan>{WEEK.SUNDAY}:</LeftSpan>
                    <RightSpan>{operationHours[WEEK.SUNDAY]}</RightSpan>
                  </StoreInfoRow>
                )}
                <StoreInfoRow>
                  <LeftSpan data-cy="contact-phone">Call or text:</LeftSpan>
                  <RightSpan data-cy="phone">
                    {IS_MOBILE ? (
                      <TextUs className={'contrast'} phoneNumber={facility?.phone}>
                        Click here
                      </TextUs>
                    ) : (
                      <span>{facility?.phone}</span>
                    )}
                  </RightSpan>
                </StoreInfoRow>
                {!IS_MOBILE && (
                  <StoreInfoRow>
                    <LeftSpan data-cy="contact-chat">Live chat:</LeftSpan>
                    <RightSpan data-cy="chat-click-here">
                      <a
                        href="#"
                        className={'contrast'}
                        onClick={handleOpenLiveChat}
                        data-cy="chat-link"
                        aria-label="Click here for live chat"
                      >
                        Click here
                      </a>
                    </RightSpan>
                  </StoreInfoRow>
                )}
                <StoreInfoRow>
                  <LeftSpan data-cy="contact-email">Email:</LeftSpan>
                  <RightSpan data-cy="email">
                    <a
                      href={`mailto:${CAPSULE_HELP_EMAIL}`}
                      className={'contrast'}
                      aria-label="Click here to contact by email"
                    >
                      {CAPSULE_HELP_EMAIL}
                    </a>
                  </RightSpan>
                </StoreInfoRow>
                <StoreInfoRow>
                  <LeftSpan data-cy="pharmacy-address">Address:</LeftSpan>
                  <RightSpan data-cy="capsule-address">
                    {facility?.address?.name}
                    <br />
                    {facility?.address?.address}
                    <br />
                    {`${facility?.address?.city}, ${facility?.address?.state} ${facility?.address?.zipCode}`}
                  </RightSpan>
                </StoreInfoRow>
              </StoreInfoContainer>
            </CardItem>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Contact;
